import * as Sentry from '@sentry/nextjs';
import {observer} from 'mobx-react';
import dynamic from 'next/dynamic';
import React, {useEffect, useRef, useState} from 'react';
import CoBrandedLogo from '../components/CoBrandedLogo/CoBrandedLogo';
import {usePartnerBrandings} from '../components/CoBrandedLogo/usePartnerBrandings';
import CustomerInformationV2 from '../components/CustomerInformationV2';
import Feature from '../components/FeatureFlag/Flags';
import useFeatureFlag from '../components/FeatureFlag/useFeatureFlag';
import {useStore} from '../components/StoreProvider';
import {actions, categories, pushEvent} from '../helpers/analyticsEvents';
import useDoNotSell from '../helpers/useDoNotSell';
import SlidesWithProgressBar from '../layouts/SlidesWithProgressBar/SlidesWithProgressBar';
import SlideContainer from '../slidesParts/SlideContainer/SlideContainer';
import {serialize} from '../store/sync/serialization';
import styles from '../styles/basic.module.scss';

const loadingStatus = () => <div>Loading...</div>;

const GoalsPage = dynamic(() => import('../components/IntentBuilder/GoalsPage'), {
    loading: loadingStatus,
    ssr: false,
});
const RevertedGoals = dynamic(() => import('../slidesParts/RevertedGoals'), {
    loading: loadingStatus,
    ssr: false,
});

export {getStaticProps} from '@amplify/abflag';

const BasicPage: React.FC = () => {
    const doNotSell = useDoNotSell();
    const store = useStore();
    const {userData, logStore} = store;
    const phase = userData.phase1;
    const intentBuilder3266 = useFeatureFlag(Feature.intentBuilder3266);
    const intentBuilder4456 = useFeatureFlag(Feature.intentBuilder4456);
    const params = new URLSearchParams(typeof window !== 'undefined' && window.location.search);
    const source = params.get('utm_source')?.toLowerCase();
    const session = params.get('session');
    const {logoMap, strapiKeys} = usePartnerBrandings();
    const isUseCoBrandedLogo = !!source && !!logoMap[source];
    const isHardcodedOnlyPartner = isUseCoBrandedLogo && !!source && !strapiKeys.has(source);

    const hubspotFormRef = useRef<HTMLFormElement | null>(null);
    const isFormLPV1 = store.userData.experiment?.newLandingPage4050 === true;
    const [forceAutoSlide, setForceAutoSlide] = useState(false);

    if (!userData.wtStartTime) {
        userData.updateField('wtStartTime', new Date().getTime());
    }

    useEffect(() => {
        // Only fetch if we have a session and don't already have user data
        if (!session || store.userData.firstName || store.userData.email) {
            return; // Skip if no session or user already has data
        }

        const fetchSession = async () => {
            try {
                const response = await fetch(`/api/partnerSubmission/${session}`);

                if (!response.ok) {
                    // Alert Sentry but don't show user error
                    Sentry.captureMessage('Partner session fetch failed', {
                        level: 'warning',
                        tags: {
                            component: 'basic-page',
                            action: 'session-prefill',
                        },
                        extra: {
                            sessionId: session,
                            statusCode: response.status,
                            statusText: response.statusText,
                        },
                    });
                    return;
                }

                const {data} = await response.json();

                // Only set if the field is currently empty
                if (data.firstName && !store.userData.firstName) {
                    store.userData.updateField('firstName', data.firstName);
                }
                if (data.lastName && !store.userData.lastName) {
                    store.userData.updateField('lastName', data.lastName);
                }
                if (data.email && !store.userData.email) {
                    store.userData.updateField('email', data.email);
                }
                if (data.phone && !store.userData.phone) {
                    store.userData.updateField('phone', data.phone);
                }
                if (data.gender && !store.userData.phase1.gender) {
                    store.userData.phase1.updateField('gender', data.gender);
                }
                if (data.dateOfBirth && !store.userData.phase1.dateOfBirth) {
                    store.userData.phase1.updateField('dateOfBirth', data.dateOfBirth);
                }
                if (data.zipCode && !store.userData.phase1.zipCode) {
                    store.userData.phase1.updateField('zipCode', data.zipCode);
                }
                if (data.additionalData?.leadId && !store.userData.partners.affiliateId) {
                    store.userData.partners.affiliateId = data.additionalData.leadId;
                }

                Sentry.addBreadcrumb({
                    message: 'Partner session prefill successful',
                    category: 'user-action',
                    data: {
                        sessionId: session,
                        fieldsPrefilledCount: Object.keys(data).length,
                    },
                });
            } catch (error) {
                Sentry.captureException(error, {
                    tags: {
                        component: 'basic-page',
                        action: 'session-prefill',
                    },
                    extra: {
                        sessionId: session,
                        errorMessage: error.message,
                    },
                });

                console.log('Partner session prefill failed silently:', error);
            }
        };

        fetchSession();
    }, [session]); // Include session as dependency

    useEffect(() => {
        logStore.logAction({
            action: 'basic-1st',
            data: serialize(store.userData),
        });
    }, []);

    useEffect(() => {
        if (!isUseCoBrandedLogo) return;

        if (intentBuilder3266 && !phase.intentGoals.length) {
            phase.updateField('intentGoals')('protection');
        } else if (!intentBuilder3266 && phase.features.length === 1) {
            phase.updateField('features')(['incomeReplacement', 'savings']);
        }
    }, [isUseCoBrandedLogo, intentBuilder3266]);

    const resetDependentFields = (value: string) => {
        if (!phase.intentGoals) {
            return;
        }

        const resetFields: Record<string, [] | string | number> = {};

        if (value === 'growth') {
            Object.assign(resetFields, {
                protectionGoals: [],
                incomeDependents: '',
                numberOfChildren: '',
                mortgageAmount: 100000,
                coverageScenarios: [],
            });
        } else if (value === 'protection') {
            Object.assign(resetFields, {
                wealthGoals: [],
                growCashValue: 500000,
                investmentVehicles: [],
                taxAdvantagedAccounts: [],
                healthEmergencySavings: 50000,
            });
        }

        Object.entries(resetFields).forEach(([key, value]) => {
            phase.updateField(key)(value);
        });
    };

    return (
        <>
            <SlidesWithProgressBar route={'basic'} isHideAmplifyLogo={false}>
                {isFormLPV1 && (
                    <SlideContainer
                        slideName="formLPV1-slide"
                        disableAutoSlide
                        validation={() => false}
                        withDrawer={true}
                        noMargin
                        noPadding
                        withoutLimit
                        hideNavigation
                        onView={() => {
                            pushEvent(categories.leadForm, actions.landingLeadFormView);
                        }}
                    >
                        <CustomerInformationV2
                            onNextClick={async () => {
                                hubspotFormRef.current.dispatchEvent(
                                    new Event('submit', {bubbles: true, cancelable: true}),
                                );
                            }}
                        />
                    </SlideContainer>
                )}
                {intentBuilder4456 ? (
                    <SlideContainer
                        withoutLimit
                        analyticsProps={{
                            action: 'wt_intent_goals_2026',
                        }}
                        slideName="insurance-goals-v2"
                        validation={() => phase.intentGoals !== ''}
                        forceAutoSlide={forceAutoSlide}
                        setForceAutoSlide={setForceAutoSlide}
                        onView={() => {
                            pushEvent(categories.intentGoals2026, actions.pageView);
                        }}
                        nextAction={() => {
                            setForceAutoSlide(false);
                            pushEvent(categories.intentGoals2026, actions.nextBtn);
                            return true;
                        }}
                        backAction={() => {
                            setForceAutoSlide(false);
                            pushEvent(categories.intentGoals2026, actions.backBtn);
                            return true;
                        }}
                        noPadding
                        noMargin
                    >
                        <GoalsPage
                            onChange={(v) => {
                                resetDependentFields(v);
                                phase.updateField('intentGoals')(v);
                            }}
                            isCoBrandedPage={isUseCoBrandedLogo}
                            showInlineLogo={isHardcodedOnlyPartner}
                            utmSource={source}
                            setForceAutoSlide={setForceAutoSlide}
                        />
                    </SlideContainer>
                ) : intentBuilder3266 ? (
                    <SlideContainer
                        withoutLimit
                        analyticsProps={{
                            action: 'wt_intent_goals',
                        }}
                        slideName="insurance-goals"
                        validation={() => phase.intentGoals !== ''}
                        onView={() => {
                            pushEvent(categories.intentGoals, actions.pageView);
                        }}
                        nextAction={() => {
                            pushEvent(categories.intentGoals, actions.nextBtn);
                            return true;
                        }}
                        backAction={() => {
                            pushEvent(categories.intentGoals, actions.backBtn);
                            return true;
                        }}
                        noPadding
                        noMargin
                    >
                        <GoalsPage
                            onChange={(v) => {
                                resetDependentFields(v);
                                phase.updateField('intentGoals')(v);
                            }}
                            isCoBrandedPage={isUseCoBrandedLogo}
                            showInlineLogo={isHardcodedOnlyPartner}
                            utmSource={source}
                        />
                    </SlideContainer>
                ) : (
                    <SlideContainer
                        slideName="features"
                        className={styles.featuresSlide}
                        validation={() =>
                            !!phase.features.length &&
                            (phase.features.length === 1 ? phase.features[0] !== undefined : true)
                        }
                        nextAction={() => {
                            pushEvent(categories.takeFeature, actions.nextBtn);
                            return true;
                        }}
                        backAction={() => {
                            pushEvent(categories.takeFeature, actions.backBtn);
                            return true;
                        }}
                        onView={() => {
                            const startRef = store.userData.getField('startRef');
                            pushEvent(
                                categories.takeFeature,
                                actions.pageView,
                                startRef?.[0] && {startRef: startRef?.[0]},
                            );
                            return true;
                        }}
                    >
                        {isHardcodedOnlyPartner && <CoBrandedLogo sx={{mb: 3}} source={source} />}
                        <RevertedGoals />
                    </SlideContainer>
                )}
            </SlidesWithProgressBar>
            <form ref={hubspotFormRef} id="walkthroughLandingPageLeadForm">
                <div style={{visibility: 'hidden'}}>
                    <input name="firstname" type="text" value={userData.firstName} readOnly />
                    <input name="lastname" type="text" value={userData.lastName} readOnly />
                    <input name="phone" type="text" value={'+1 ' + userData.phone} readOnly />
                    <input name="email" type="email" value={userData.email} readOnly />
                    <input name="gender" type="text" value={phase.gender} readOnly />
                    <input name="date_of_birth" type="text" value={phase.dateOfBirth} readOnly />
                    {userData.referral.partnerUserId && (
                        <input name="partner_user_id" type="text" value={userData.referral.partnerUserId} readOnly />
                    )}
                    {userData.referral.callbackUrl && (
                        <input name="partner_callback_url" type="text" value={userData.referral.callbackUrl} readOnly />
                    )}
                    {userData.partners.affiliateId && (
                        <input name="affiliate_id" type="text" value={userData.partners.affiliateId} readOnly />
                    )}
                    {/* Suppression flag for lead sale/sharing. Duplicated on the backend lead
                        payload (see LogStore) in case HubSpot's form capture does not fire.

                        Only rendered when set, never as "false": HubSpot form submissions
                        overwrite properties, so posting false would erase an earlier opt-out
                        when the same person returns on a browser without GPC. Absence means
                        no opt-out on record; the flag can be set but never cleared this way. */}
                    {doNotSell && <input name="do_not_sell" type="text" value="true" readOnly />}
                    <input type="submit" />
                </div>
            </form>
        </>
    );
};

export default observer(BasicPage);
